import React, { useCallback } from 'react';
import PropTypes from 'prop-types';
import {
    Box,
    Typography,
    Button,
    Divider,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditableAssociationRow from './EditableAssociationRow';


/**
 * EditableAssociation Component
 * 
 * Renders an editable form for many-to-many associations with custom properties.
 * Handles add/remove/modify operations and syncs changes back to parent.
 * @param {Array} assignments - Current list of assignment objects with _key and properties
 * @param {Array} allRecords - Full list of available records for selection
 * @param {Boolean} loadingRecords - Whether the available records are still loading
 * @param {Object} config - Configuration for fields, labels, and behavior
 * @param {Function} onChange - Callback when assignments change
 */
const EditableAssociation = ({
    assignments = [],
    allRecords = [],
    loadingRecords = false,
    config = {},
    onChange = () => { },
}) => {
    const {
        fields = [],
        getOptionLabel = (option) => option?.name || '',
        getAvailableRecords,
        onlyAllowUnique = true,
        idField = 'id',
        emptyStateText = 'No records assigned.',
        addButtonText = 'Assign Record',
    } = config;

    // Get available records, filtering out duplicates if needed
    const getFilteredOptions = useCallback((currentKey) => {
        let filtered = allRecords;

        // Apply custom filter if provided
        if (getAvailableRecords) {
            filtered = getAvailableRecords(filtered, currentKey);
        }

        // Prevent duplicates if configured
        if (onlyAllowUnique) {
            const assignedIds = assignments
                .filter(a => a._key !== currentKey && a[idField] != null)
                .map(a => String(a[idField]));

            const currentAssignedValue = assignments.find(a => a._key === currentKey)?.[idField];

            filtered = filtered.filter(r => {
                const rid = String(r?.[idField]);
                if (!assignedIds.includes(rid)) return true;
                if (currentAssignedValue == null) return false;
                return String(currentAssignedValue) === rid;
            });
        }

        return filtered;
    }, [assignments, allRecords, getAvailableRecords, onlyAllowUnique, idField]);

    const handleAddRow = () => {
        const newAssignment = {
            _key: `${Date.now()}-${Math.random().toString(36)}`,  // Unique key, that's all
            [idField]: null,
        };
        // Initialize all field properties with null/false defaults
        fields.forEach(field => {
            if (field.type === 'checkbox') {
                newAssignment[field.name] = false;
            } else {
                newAssignment[field.name] = null;
            }
        });
        onChange([...assignments, newAssignment]);
    };

    const handleUpdateRow = (key, updates) => {
        onChange(
            assignments.map(a =>
                a._key === key
                    ? { ...a, ...updates }
                    : a
            )
        );
    };

    const handleRemoveRow = (keyToRemove) => {
        onChange(assignments.filter(a => a._key !== keyToRemove));
    };

    if (assignments.length === 0) {
        return (
            <Box sx={{ p: 3 }}>
                <Typography color="text.secondary" sx={{ mb: 2 }}>
                    {emptyStateText}
                </Typography>
                <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleAddRow}
                    startIcon={<AddIcon />}
                >
                    {addButtonText}
                </Button>
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            {/* Headers Row */}
            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, mb: 1, px: 0 }}>
                <Typography
                    variant="body2"
                    sx={{ color: 'primary.main', fontWeight: 600, minWidth: 280, maxWidth: 350 }}
                >
                    Record
                </Typography>
                {fields.map(field => (
                    <Typography
                        key={field.name}
                        variant="body2"
                        sx={{
                            color: 'primary.main',
                            fontWeight: 600,
                            width: field.width,
                            flex: field.flex,
                            textAlign: field.type === 'checkbox' ? 'center' : 'left',
                        }}
                    >
                        {field.label}
                    </Typography>
                ))}
                <Box sx={{ width: 40 }} /> {/* Spacer for delete button */}
            </Box>

            {assignments.map((assignment, index) => (
                <Box key={assignment._key}>
                    {index > 0 && <Divider sx={{ my: 2 }} />}
                    <EditableAssociationRow
                        assignment={assignment}
                        fields={fields}
                        allRecords={allRecords}
                        loadingRecords={loadingRecords}
                        getFilteredOptions={getFilteredOptions}
                        idField={idField}
                        dataField={config.dataField}
                        onUpdate={(updates) => handleUpdateRow(assignment._key, updates)}
                        onRemove={() => handleRemoveRow(assignment._key)}
                        getOptionLabel={getOptionLabel}
                    />
                </Box>
            ))}

            <Button
                variant="outlined"
                fullWidth
                onClick={handleAddRow}
                startIcon={<AddIcon />}
                sx={{ mt: 3 }}
            >
                {addButtonText}
            </Button>
        </Box>
    );
};

EditableAssociation.propTypes = {
    assignments: PropTypes.arrayOf(
        PropTypes.shape({
            _key: PropTypes.string.isRequired,
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        })
    ),
    allRecords: PropTypes.array,
    loadingRecords: PropTypes.bool,
    config: PropTypes.shape({
        fields: PropTypes.arrayOf(
            PropTypes.shape({
                name: PropTypes.string.isRequired,
                label: PropTypes.string.isRequired,
                type: PropTypes.oneOf(['text', 'checkbox', 'select']).isRequired,
                width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
                flex: PropTypes.number,
                placeholder: PropTypes.string,
                multiline: PropTypes.bool,
                minRows: PropTypes.number,
                maxRows: PropTypes.number,
            })
        ),
        getOptionLabel: PropTypes.func,
        getAvailableRecords: PropTypes.func,
        idField: PropTypes.string,
        dataField: PropTypes.string,
        onlyAllowUnique: PropTypes.bool,
        emptyStateText: PropTypes.string,
        addButtonText: PropTypes.string,
    }),
    onChange: PropTypes.func,
};

export default EditableAssociation;
